import React from 'react';
import { ComboboxItem } from '@mantine/core';
import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { MultiSelect } from 'src/design/atoms/MultiSelect';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { JobUserDto } from 'src/api/FiretrolApiClient';
import { useJobs } from 'src/hooks/useJobs';
import { toMultiSelectValue } from 'src/design/atoms/toMultiSelectValue';

export interface IJobUserSelectV2Props {
  onChange: (value: JobUserDto[] | null) => void;
  value: JobUserDto[] | null;
  hideLabel?: boolean;
  placeholder?: string;
  hidden?: boolean;
  disabled?: boolean;
}

/** @registry Multi-select dropdown of job users (V2 API). */
export const JobUserSelectV2: React.FC<IJobUserSelectV2Props> = ({
  onChange,
  disabled = false,
  hidden = false,
  value,
  hideLabel = false,
  placeholder = 'Select users...',
}) => {
  const { teamV2 } = useJobs();

  const userData = React.useMemo(() => {
    return teamV2.map((user) => ({
      value: String(user.user.id),
      label: user.user?.fullName || '',
    }));
  }, [teamV2]);

  const renderOption = ({
    option,
    checked,
  }: {
    option: ComboboxItem;
    checked: boolean;
  }) => {
    const user = teamV2.find((u) => String(u.user.id) === option.value);
    if (!user) return option.label;

    return (
      <Group justify="space-between" wrap="nowrap" w="100%">
        <Group gap="xs" wrap="nowrap">
          <Checkbox checked={checked} onChange={() => {}} tabIndex={-1} />
          <span>{user.user.fullName}</span>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {user.user.userOrganizationRole
            ?.filter((r) => !!r.organizationRole)
            .map((r) => r.organizationRole)
            .map((r) => {
              return (
                <Badge color="blue" variant="filled" key={r!.id}>
                  {r!.name}
                </Badge>
              );
            })}
        </Group>
      </Group>
    );
  };

  const handleChange = (selectedValues: string[]) => {
    if (selectedValues.length > 0) {
      const selectedUsers = selectedValues
        .map((id) => teamV2.find((u) => String(u.user.id) === id))
        .filter((u): u is JobUserDto => u !== undefined);
      onChange(selectedUsers);
    } else {
      onChange([]);
    }
  };

  if (hidden)
    return <div style={{ marginBottom: 'var(--mantine-spacing-xl)' }}></div>;
  return (
    <MultiSelect
      label={!hideLabel ? 'User' : undefined}
      placeholder={placeholder}
      data={userData}
      value={toMultiSelectValue(
        (value ?? []).map((v) => String(v.user.id)),
        userData,
      )}
      onChange={handleChange}
      disabled={disabled}
      clearable
      searchable
      renderOption={renderOption}
      mb="xl"
    />
  );
};
